import type { FC, ReactNode } from 'react';
import { useRef } from 'react';
import { ColorDropdown, DropdownActionButton } from '@george-labs.com/design-system';
import type { COLOR } from '@george-labs.com/design-system/types/utilsTS/color';

import { useGlobalColor } from '@shared/core-ui-layout';
import { t } from '@luna/i18n';
import { useActiveCustomizationFeature } from '@shared/products-core';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import { useNotifications } from '@shared/snackbar';

type Props = {
    onColorSave: (color: COLOR) => Promise<void>;
    onCancelColorChange?: (originalColor: COLOR) => void;
    toggle: ReactNode;
    actionButtonDataCy: string;
    'data-cy': string;
    colorChangeContext: 'overview' | 'stripeDropdown' | 'details';
    accountType?: string;
} & Parameters<typeof trackEvent2<'productCustomisationChanges', 'productColourChangeStarted'>>[2];

export const ProductColorChangeDropdown: FC<Props> = ({
    onColorSave,
    toggle,
    actionButtonDataCy,
    'data-cy': dataCy,
    colorChangeContext,
    accountType = '',
    trigger,
}) => {
    const { isColorChangeCustomizationActive, resetActiveCustomization } =
        useActiveCustomizationFeature();
    const { displaySuccess } = useNotifications('product-settings-details-dashboard-appearance');
    const { setValue: setGlobalColor, value } = useGlobalColor();

    const popoverRef = useRef<HTMLDivElement | null>(null);
    const prevFocusedRef = useRef<HTMLElement | null>(null);

    const handleColorSave = (newColor: COLOR) => {
        onColorSave(newColor).then(() => {
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                productType: accountType,
                customisationChanges: ['color'],
                colorName: newColor,
            });
            displaySuccess(t('products.settings.details.colorChanged'));
            georgeStats.trackEvent(colorChangeContext, 'colorChanged', {
                selectedColor: newColor,
                accountType,
            });
        });
    };

    const handleColorPickerOpen = () => {
        georgeStats.trackEvent(colorChangeContext, 'colorPickerOpened', { initialColor: value });
    };

    const handleColorChangeCancel = (originalColor: COLOR) => {
        georgeStats.trackEvent(colorChangeContext, 'colorChangeCancelled', {
            initialSelectedColor: originalColor,
        });
        setGlobalColor(originalColor);
    };

    const handleCloseColorChangeDropdown = () => {
        prevFocusedRef.current?.focus();
        prevFocusedRef.current = null;
        isColorChangeCustomizationActive && resetActiveCustomization();
    };

    const handleColorChangeDropdownOpen = () => {
        prevFocusedRef.current = document.activeElement as HTMLElement;
        popoverRef.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.focus();

        trackEvent2('productCustomisationChanges', 'productColourChangeStarted', {
            trigger,
        });
    };

    return (
        <ColorDropdown
            popoverRef={popoverRef}
            isOpen={isColorChangeCustomizationActive}
            key={`${value === undefined}`}
            data-cy={dataCy}
            alignment={ColorDropdown.ALIGNMENT.LEFT}
            toggle={toggle}
            selectedColor={value}
            actionButton={
                <DropdownActionButton data-cy={actionButtonDataCy}>
                    {t('app.save')}
                </DropdownActionButton>
            }
            onChange={setGlobalColor}
            onSave={handleColorSave}
            onCancel={handleColorChangeCancel}
            onClose={handleCloseColorChangeDropdown}
            onBeforeOpen={handleColorPickerOpen}
            onOpen={handleColorChangeDropdownOpen}
        />
    );
};
